import { Stack } from '@chakra-ui/react'
import type { StackProps } from '@chakra-ui/react'
import { useViewport } from '@xyflow/react'
import type { FC } from 'react'
import { memo } from 'react'

import { Card } from '../components'
import CardLabels from '../components/cardLabels'

import useGetObject from '@app/hooks/useGetObject'
import useIsAnonymousUser from '@app/hooks/useIsAnonymousUser'
import BasicCardTypePopover from '@app/next/basicCardTypePopover'
import DomainObjectStatusPopover from '@app/next/domainObjectStatusPopover'
import WithLabel from '@app/next/forms/withLabel'
import CollapsedNodes from '@app/pages/strategies/components/nodes/components/collapsedNodes'
import { scaleSizeToMapZoom } from '@app/pages/strategies/components/nodes/helpers'
import SimImpactsSection from '@app/pages/strategies/components/nodes/simulator/simImpactsSection'
import cardFontSize from '@app/shared/cards/cardFontSize'
import cardIconBoxSize from '@app/shared/cards/cardIconBoxSize'
import cardMetadata from '@app/shared/cards/cardMetadata'
import CardDetailsLink from '@app/shared/cards/components/cardDetailsLink'
import CardGoalsCTA from '@app/shared/cards/components/cardGoalsCTA'
import CardSection from '@app/shared/cards/components/cardSection'
import CardSharedActions from '@app/shared/cards/components/cardSharedActions'
import CardTitle from '@app/shared/cards/components/cardTitle'
import CardTypeSection from '@app/shared/cards/components/cardTypeSection'
import MarkdownDisplay from '@app/shared/markdownDisplay'
import PulsingCircle from '@app/shared/pulsingCircle'
import type { DomainNode, MapMode } from '@app/types'
import type { BasicCard as BasicCardType } from '@graphql/types'

interface Props extends StackProps {
  strategyId: string
  basicCard?: BasicCardType
  node?: DomainNode
  onClick?: () => void
  onDoubleClick?: () => void
  mapMode?: MapMode
}

const BasicCard: FC<Props> = ({ basicCard, node, strategyId, mapMode = 'build', ...rest }) => {
  const isAnonymousUser = useIsAnonymousUser(strategyId)
  const simulationMode = mapMode === 'simulation'
  const { size } = cardMetadata(node)
  const {
    cardTypeId,
    description,
    externalUrl,
    externalUrlTitle,
    goalHypothesis,
    id,
    labels,
    recentActivity,
    results,
    showDescription,
    showGoalHypothesis,
    showResults
  } = basicCard
  const { zoom } = useViewport()
  const cardType = useGetObject(cardTypeId, 'cardType')

  const route = isAnonymousUser ? null : `/strategy/${strategyId}/map/basicCard/${id}`

  const pulseSize = scaleSizeToMapZoom(24, zoom, 44)
  const fontSize = cardFontSize('md', size)
  const boxSize = cardIconBoxSize(5, size)

  const showGoalsCTA = !isAnonymousUser && mapMode === 'goals' && !goalHypothesis

  const nodeColor = node?.metadata?.color
  const cardTypeColor = cardType?.color ? `${cardType.color}.500` : 'gray.500'

  return (
    <Card color={nodeColor || cardTypeColor} {...rest}>
      <CardTypeSection route={route} size={size}>
        <BasicCardTypePopover basicCard={basicCard} fontSize={fontSize} boxSize={boxSize} />
      </CardTypeSection>
      <CardSection size={size}>
        <PulsingCircle isActive={!!recentActivity} size={pulseSize} link={`basicCard/${id}/events`} />
        <Stack gap={size === 'large' ? 6 : 2}>
          <CardTitle domainObject={basicCard} name="name" size={size} />
          {showDescription && description && <MarkdownDisplay text={description} fontSize={fontSize} />}
          {showGoalHypothesis && goalHypothesis && (
            <WithLabel showLabel={!!goalHypothesis} label="Goal / Hypothesis">
              <MarkdownDisplay text={goalHypothesis} fontSize={fontSize} />
            </WithLabel>
          )}
          {showResults && results && (
            <WithLabel showLabel={!!results} label="Results">
              <MarkdownDisplay text={results} fontSize={fontSize} />
            </WithLabel>
          )}
          {externalUrl && <CardDetailsLink url={externalUrl} text={externalUrlTitle} size={size} />}
        </Stack>
      </CardSection>
      {showGoalsCTA && <CardGoalsCTA link={`basicCard/${id}/details`} size={size} />}
      {labels?.length && <CardLabels fieldName="labels" domainObject={basicCard} size={size} />}
      <CardSharedActions domainObject={basicCard} size={size} route={route}>
        <DomainObjectStatusPopover domainObject={basicCard} mx={2} />
      </CardSharedActions>
      {node && <CollapsedNodes node={node} />}
      {simulationMode && <SimImpactsSection node={node} size={size} />}
    </Card>
  )
}

export default memo(BasicCard)
